import { cn } from "~/lib/utils"

// Forest Design System buttons — the site-wide defaults, implemented 1:1 from
// the Figma component sets (Forest - Design System, page "Components",
// section "Buttons"). Variants:
//
//   primary    Main CTA. Lime fill, arrow, hover shifts to chartreuse.
//   secondary  Olive outline at rest (Figma 4:177); hover fills (hay on
//              light, moss on dark), pressed thickens the border to 1.5px.
//   text       Inline label + arrow; hover recolors + underlines.
//
// `surface` picks the palette for the ground the button sits on ("light"
// pages vs the dark forest bands). Passing `state` freezes a specimen in that
// exact visual state — that's what the /design review board uses; omitting it
// gives the live styles wired to :hover / :active / :disabled.
//
// Hover/pressed borders are drawn with inset box-shadows instead of real
// borders so state changes never shift layout.

export type ButtonVariant = "primary" | "secondary" | "text"
export type Surface = "light" | "dark"
export type ForceState = "default" | "hover" | "pressed" | "disabled"

// Exact vector from the Figma export (ArrowForwardOutlined, node 4:166),
// fill switched to currentColor so the label color drives the icon per state.
// `direction="down"` rotates the same glyph a quarter turn — used when the
// link scrolls to a section on the current page rather than navigating away.
export function ArrowIcon({
  className,
  direction = "right",
}: {
  className?: string
  direction?: "right" | "down"
}) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      className={cn("shrink-0", direction === "down" && "rotate-90", className)}
      aria-hidden
    >
      <path
        d="M8 2.66667L7.06 3.60667L10.78 7.33333H2.66667V8.66667H10.78L7.06 12.3933L8 13.3333L13.3333 8L8 2.66667Z"
        fill="currentColor"
      />
    </svg>
  )
}

const labelBase =
  "inline-flex items-center justify-center font-sans text-[16px] font-medium leading-[1.2] whitespace-nowrap select-none"

const focusLight =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest focus-visible:ring-offset-2"
const focusDark =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime focus-visible:ring-offset-2 focus-visible:ring-offset-forest"

// Primary reads the same on both surfaces (lime carries on cream and forest).
const primaryStates: Record<ForceState, string> = {
  default: "bg-lime text-black rounded-[4px]",
  hover: "bg-chartreuse text-forest/80 rounded-[6px]",
  pressed: "bg-chartreuse text-black rounded-[4px]",
  disabled: "bg-mute text-hay/80 rounded-[4px]",
}
const primaryLive =
  "bg-lime text-black rounded-[4px] cursor-pointer transition-[background-color,border-radius] duration-150 " +
  "hover:bg-chartreuse hover:text-forest/80 hover:rounded-[6px] " +
  "active:bg-chartreuse active:text-black active:rounded-[4px] " +
  "disabled:bg-mute disabled:text-hay/80 disabled:cursor-default"

// Borders are inset box-shadows (no layout shift). Rest + hover = 1px
// #66640F; pressed = 1.5px (#66640F on light, #F1EDEA on dark). Hover adds a
// fill; disabled is a flat #E5E5E5 with no border.
const secondaryStates: Record<Surface, Record<ForceState, string>> = {
  light: {
    default: "text-forest shadow-[inset_0_0_0_1px_#66640F]",
    hover: "bg-hay text-forest shadow-[inset_0_0_0_1px_#66640F]",
    pressed: "bg-hay text-forest shadow-[inset_0_0_0_1.5px_#66640F]",
    disabled: "bg-[#E5E5E5] text-mute",
  },
  dark: {
    default: "text-cream shadow-[inset_0_0_0_1px_#66640F]",
    hover: "bg-moss text-cream shadow-[inset_0_0_0_1px_#66640F]",
    pressed: "bg-moss text-cream shadow-[inset_0_0_0_1.5px_#F1EDEA]",
    disabled: "bg-[#E5E5E5] text-mute",
  },
}
const secondaryLive: Record<Surface, string> = {
  light:
    "text-forest shadow-[inset_0_0_0_1px_#66640F] cursor-pointer transition-[background-color,box-shadow] duration-150 " +
    "hover:bg-hay " +
    "active:bg-hay active:shadow-[inset_0_0_0_1.5px_#66640F] " +
    "disabled:bg-[#E5E5E5] disabled:text-mute disabled:shadow-none disabled:cursor-default",
  dark:
    "text-cream shadow-[inset_0_0_0_1px_#66640F] cursor-pointer transition-[background-color,box-shadow] duration-150 " +
    "hover:bg-moss " +
    "active:bg-moss active:shadow-[inset_0_0_0_1.5px_#F1EDEA] " +
    "disabled:bg-[#E5E5E5] disabled:text-mute disabled:shadow-none disabled:cursor-default",
}

const textStates: Record<Surface, Record<ForceState, string>> = {
  light: {
    default: "text-black",
    hover: "text-chartreuse shadow-[inset_0_-1px_0_#829300]",
    pressed: "text-black font-semibold",
    disabled: "text-mute opacity-50",
  },
  dark: {
    default: "text-cream",
    hover: "text-chartreuse shadow-[inset_0_-1px_0_#829300]",
    pressed: "text-hay font-semibold",
    disabled: "text-mute opacity-50",
  },
}
const textLive: Record<Surface, string> = {
  light:
    "text-black cursor-pointer transition-[color,box-shadow] duration-150 " +
    // chartreuse-deep: chartreuse itself is below AA contrast on light grounds
    "hover:text-chartreuse-deep hover:shadow-[inset_0_-1px_0_#829300] " +
    "active:text-black active:font-semibold active:shadow-none",
  dark:
    "text-cream cursor-pointer transition-[color,box-shadow] duration-150 " +
    "hover:text-chartreuse hover:shadow-[inset_0_-1px_0_#829300] " +
    "active:text-hay active:font-semibold active:shadow-none",
}

export function buttonClasses({
  variant = "primary",
  surface = "light",
  state,
}: {
  variant?: ButtonVariant
  surface?: Surface
  state?: ForceState
}): string {
  const focus = surface === "dark" ? focusDark : focusLight
  switch (variant) {
    case "primary":
      return cn(
        labelBase,
        "gap-2.5 p-4",
        state ? primaryStates[state] : cn(primaryLive, focus),
      )
    case "secondary":
      return cn(
        labelBase,
        "gap-2.5 rounded-[4px] p-4",
        state
          ? secondaryStates[surface][state]
          : cn(secondaryLive[surface], focus),
      )
    case "text":
      return cn(
        labelBase,
        "gap-1",
        state ? textStates[surface][state] : cn(textLive[surface], focus),
      )
  }
}

type CommonProps = {
  variant?: ButtonVariant
  surface?: Surface
  state?: ForceState
  /** Primary carries the Figma arrow by default; opt in elsewhere. */
  withArrow?: boolean
  /** "down" when the link scrolls to a section instead of navigating. */
  arrowDirection?: "right" | "down"
}

export function Button({
  variant = "primary",
  surface = "light",
  state,
  withArrow,
  arrowDirection,
  className,
  children,
  disabled,
  ...props
}: CommonProps & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const arrow = withArrow ?? variant !== "secondary"
  return (
    <button
      type="button"
      disabled={disabled || state === "disabled"}
      className={cn(buttonClasses({ variant, surface, state }), className)}
      {...props}
    >
      {children}
      {arrow && <ArrowIcon direction={arrowDirection} />}
    </button>
  )
}

export function ButtonLink({
  variant = "primary",
  surface = "light",
  state,
  withArrow,
  arrowDirection,
  className,
  children,
  ...props
}: CommonProps & React.AnchorHTMLAttributes<HTMLAnchorElement>) {
  const arrow = withArrow ?? variant !== "secondary"
  return (
    <a
      className={cn(buttonClasses({ variant, surface, state }), className)}
      {...props}
    >
      {children}
      {arrow && <ArrowIcon direction={arrowDirection} />}
    </a>
  )
}

/* ── Nav Button (Figma 7:374) — pill-shaped nav item ─────────────────── */

export type NavState = "default" | "hover" | "selected"

const navStates: Record<Surface, Record<NavState, string>> = {
  dark: {
    default: "text-cream",
    hover: "text-chartreuse",
    selected: "bg-moss text-white",
  },
  light: {
    default: "text-forest",
    hover: "text-moss",
    selected: "bg-moss text-hay",
  },
}
const navLive: Record<Surface, string> = {
  dark: "text-cream cursor-pointer transition-colors duration-150 hover:text-chartreuse focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime",
  light:
    "text-forest cursor-pointer transition-colors duration-150 hover:text-moss focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest",
}

export function navPillClasses({
  surface = "dark",
  state,
  selected = false,
}: {
  surface?: Surface
  state?: NavState
  selected?: boolean
}): string {
  const cls = state
    ? navStates[surface][state]
    : selected
      ? navStates[surface].selected
      : navLive[surface]
  return cn(labelBase, "rounded-full px-5 py-2.5", cls)
}

export function NavPill({
  surface = "dark",
  state,
  selected,
  className,
  children,
  ...props
}: {
  surface?: Surface
  state?: NavState
  selected?: boolean
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className={cn(navPillClasses({ surface, state, selected }), className)}
      {...props}
    >
      {children}
    </button>
  )
}

export function NavPillLink({
  surface = "dark",
  state,
  selected,
  className,
  children,
  ...props
}: {
  surface?: Surface
  state?: NavState
  selected?: boolean
} & React.AnchorHTMLAttributes<HTMLAnchorElement>) {
  return (
    <a
      className={cn(navPillClasses({ surface, state, selected }), className)}
      {...props}
    >
      {children}
    </a>
  )
}
